<script setup>
import {ref} from "vue";

defineProps(['systemMessages']);

const debugVisible = ref(false);
const copiedIndex = ref(null);
const collapsedMessages = ref(new Set());

/* Debug popup functions */
function copyMessage(message, index) {
    navigator.clipboard.writeText(message).then(() => {
        copiedIndex.value = index;
        setTimeout(() => { copiedIndex.value = null; }, 2000);
    });
}

function toggleMessage(index) {
    const set = new Set(collapsedMessages.value);
    if (set.has(index)) {
        set.delete(index);
    } else {
        set.add(index);
    }
    collapsedMessages.value = set;
}
/* End Debug popup functions */
</script>

<template>
    <div class="debug-trigger" @click.stop="debugVisible = !debugVisible">
        <transition name="debug-fade">
            <div v-if="debugVisible" class="debug-panel" @click.stop>
                <div class="debug-panel__header">
                    <span class="debug-panel__title">🛠 Debug Panel</span>
                    <a class="debug-close" @click.stop="debugVisible = false">✕</a>
                </div>

                <div class="debug-panel__section">
                    <div class="debug-panel__section-header">
                        <span>System Messages <span class="debug-badge">{{ systemMessages.length }}</span></span>
                        <a class="debug-clear" @click.stop="systemMessages = []">Clear all</a>
                    </div>
                    <div class="debug-panel__body">
                        <div v-if="systemMessages.length === 0" class="debug-empty">No messages yet</div>
                        <div v-for="(message, index) in systemMessages" :key="index" class="debug-message">
                            <div class="debug-message__header">
                                                <span class="debug-index" @click.stop="toggleMessage(index)">
                                                    <span class="debug-chevron">{{ collapsedMessages.has(index) ? '▶' : '▼' }}</span>
                                                    #{{ index + 1 }}
                                                    <span v-if="collapsedMessages.has(index)" class="debug-preview">{{ message.slice(0, 60).replace(/\s+/g, ' ') }}…</span>
                                                </span>
                                <button class="debug-copy" @click.stop="copyMessage(message, index)" :class="{ copied: copiedIndex === index }">
                                    {{ copiedIndex === index ? '✓ Copied' : 'Copy' }}
                                </button>
                            </div>
                            <pre v-if="!collapsedMessages.has(index)" class="debug-message__text">{{ message }}</pre>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<style scoped>
    /* Invisible click trigger in bottom-left corner */
    .debug-trigger {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 60px;
        height: 60px;
        z-index: 9999;
        cursor: default;
    }

    /* Debug panel floats above the trigger, outside chat flow */
    .debug-panel {
        position: absolute;
        bottom: 60px;
        left: 0;
        width: 460px;
        max-height: 75vh;
        background: #fff;
        border: 1px solid #d2dde1;
        border-radius: 8px;
        box-shadow: 0 8px 32px rgba(4, 54, 72, 0.18);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
        font-size: 12px;
        color: #043648;
    }

    .debug-panel__header {
        padding: 10px 14px;
        background: #043648;
        border-bottom: 1px solid #032e3d;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .debug-panel__title {
        font-weight: 600;
        font-size: 13px;
        color: #fff;
        letter-spacing: 0.03em;
    }

    .debug-close {
        color: #99cee0;
        cursor: pointer;
        font-size: 13px;
        line-height: 1;
    }

    .debug-close:hover {
        color: #fff;
    }

    .debug-panel__section {
        display: flex;
        flex-direction: column;
        min-height: 0;
        border-bottom: 1px solid #d4eaf2;
    }

    .debug-panel__section:last-child {
        border-bottom: none;
    }

    .debug-panel__section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 7px 14px;
        background: #ebf7fb;
        font-weight: 600;
        color: #0083b2;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        border-bottom: 1px solid #d4eaf2;
    }

    .debug-badge {
        display: inline-block;
        background: #0083b2;
        color: #fff;
        border-radius: 8px;
        padding: 0 6px;
        font-size: 10px;
        font-weight: 700;
        margin-left: 5px;
    }

    .debug-clear {
        color: #ff6a6a;
        cursor: pointer;
        font-size: 10px;
        text-transform: none;
        letter-spacing: 0;
        font-weight: 400;
    }

    .debug-clear:hover {
        color: #a50000;
        text-decoration: underline;
    }

    .debug-panel__body {
        padding: 8px 14px;
        overflow-y: auto;
        max-height: 50vh;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .debug-empty {
        color: #95a8b0;
        font-style: italic;
        font-size: 11px;
        padding: 4px 0;
    }

    .debug-message {
        display: flex;
        flex-direction: column;
        gap: 4px;
        background: #f4f4f4;
        border: 1px solid #e1e1e1;
        border-radius: 5px;
    }

    .debug-message__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px 8px;
        background: #ebf7fb;
        border-bottom: 1px solid #d4eaf2;
        gap: 8px;
        min-width: 0;
    }

    .debug-index {
        color: #0083b2;
        font-weight: 700;
        font-size: 11px;
        cursor: pointer;
        user-select: none;
        display: flex;
        align-items: center;
        gap: 4px;
        flex: 1;
        min-width: 0;
    }

    .debug-index:hover {
        color: #043648;
    }

    .debug-chevron {
        font-size: 9px;
        flex-shrink: 0;
    }

    .debug-preview {
        color: #5e7e95;
        font-weight: 400;
        font-size: 10px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
    }

    .debug-copy {
        background: #0083b2;
        color: #fff;
        border: none;
        border-radius: 4px;
        padding: 2px 8px;
        font-size: 10px;
        cursor: pointer;
        font-family: inherit;
        transition: background 0.15s;
    }

    .debug-copy:hover {
        background: #043648;
    }

    .debug-copy.copied {
        background: #1e7d3f;
    }

    .debug-message__text {
        margin: 0;
        padding: 6px 8px;
        white-space: pre-wrap;
        word-break: break-word;
        line-height: 1.5;
        color: #043648;
        font-size: 11px;
    }

    .debug-log {
        white-space: pre-wrap;
        word-break: break-word;
        color: #1e7d3f;
        margin: 0;
        line-height: 1.5;
    }

    /* Fade transition */
    .debug-fade-enter-active,
    .debug-fade-leave-active {
        transition: opacity 0.2s ease, transform 0.2s ease;
    }

    .debug-fade-enter-from,
    .debug-fade-leave-to {
        opacity: 0;
        transform: translateY(6px);
    }
</style>
